/**
 * Phone number normalisation.
 *
 * Its own module, away from `session.ts`, for one practical reason: session.ts
 * imports `../firebase`, which touches `window` at import time. A unit test for
 * this pure function should not need a browser to exist.
 */

/**
 * Indian numbers get typed every way a keypad allows: `98765 43210`,
 * `098765-43210`, `+91 98765 43210`. Firebase accepts exactly one of those, so
 * the app does the converting rather than making the user do it.
 *
 * A number that already carries some other country code is left alone. Only a
 * bare ten-digit number is assumed to be Indian, because that is the only case
 * where the assumption cannot be wrong — and getting it wrong sends the code to
 * a real phone belonging to a stranger, while the user watches a screen that
 * says it was sent.
 */
/**
 * What the phone FIELD should show, given what was just typed or autofilled.
 *
 * The field sits behind a fixed "+91" label, so an autofill that hands over a
 * full `+919163975637` renders as "+91 +919163975637" — which looks broken, and
 * is the first thing anyone sees of this app. The submitted value was always
 * correct (`e164` copes), but nobody trusts a form that displays nonsense.
 *
 * So an Indian country code is absorbed into the label, and the field keeps only
 * the local part. A DIFFERENT country code is kept in the field verbatim and
 * reported back with `hasCountryCode`, so the caller can hide the "+91" rather
 * than tell a visitor from London that their number is Indian.
 */
export function tidyPhoneInput(input: string): {
  value: string;
  hasCountryCode: boolean;
} {
  const typed = input.trim();
  const digits = typed.replace(/\D/g, "");

  if (typed.startsWith("+")) {
    // `+91` plus ten digits is ours; anything else belongs to another country.
    if (digits.length === 12 && digits.startsWith("91")) {
      return { value: digits.slice(2), hasCountryCode: false };
    }
    return { value: `+${digits}`, hasCountryCode: true };
  }

  // A leading 0 is the domestic trunk prefix, and a bare 91 in front of ten
  // more digits is the country code without its plus. Neither is part of the
  // number. Ten digits that merely START with 91 are left alone — 9163975637
  // is a real number, not a country code and eight digits.
  const local = digits.replace(/^0+/, "");
  if (local.length === 12 && local.startsWith("91")) {
    return { value: local.slice(2), hasCountryCode: false };
  }
  return { value: local, hasCountryCode: false };
}

export function e164(input: string): string {
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) return `+${digits}`;
  // A leading 0 is the domestic trunk prefix and is not part of the number.
  const local = digits.replace(/^0+/, "");
  if (local.length === 10) return `+91${local}`;
  if (local.length === 12 && local.startsWith("91")) return `+${local}`;
  return `+${local}`;
}
